import { bearerAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { domainToRegex, exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const serverUrlItem = {
  kind: "baseUrl",
  label: "Keycloak server URL",
  required: true,
  placeholder: "https://keycloak.example.com",
  target: { kind: "configuration", field: "instanceUrl" },
  helperText:
    "The base URL of your Keycloak server, including any path prefix such as /auth.",
} as const;

export const keycloak = defineProduct({
  name: "Keycloak",
  tagLine: "Identity and access management",
  logo,
  tags: ["identity"],
  variants: [
    productVariant({
      identifier: "keycloak",
      authLabel: "Access token",
      protocol: "http",
      auth: (app) => bearerAuth(app.secret.accessToken),
      steps: [
        {
          title: "Get an admin API token",
          items: [
            {
              kind: "markdown",
              content:
                "Generate an access token with the admin roles your workflows need. This option requires you to replace the token when it expires. Choose OAuth client credentials for automatic token renewal.",
            },
          ],
        },
        {
          title: "Enter your Keycloak details",
          items: [
            serverUrlItem,
            {
              kind: "input",
              label: "Access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      defaultUrlPattern: ({ instanceUrl }) => domainToRegex(instanceUrl),
      connectionTest: {
        protocol: "http",
        request: ({ instanceUrl }) => `${instanceUrl}/admin/realms`,
      },
    }),
    productVariant({
      identifier: "keycloakClientCredentials",
      authLabel: "OAuth client credentials",
      protocol: "http",
      auth: oauthAuth,
      steps: [
        {
          title: "Create a Keycloak service account",
          items: [
            {
              kind: "markdown",
              content:
                "In your realm, create an OpenID Connect client with **Client authentication** and **Service account roles** enabled. Assign the admin roles your workflows need under **Service account roles**, and include them in the client’s role scope mappings. Copy the client ID from **Settings** and the client secret from **Credentials**. Access tokens renew automatically.",
            },
          ],
        },
        {
          title: "Enter your Keycloak details",
          items: [
            serverUrlItem,
            {
              kind: "input",
              label: "Realm",
              required: true,
              placeholder: "my-realm",
              helperText: "The name of the realm containing your client.",
              target: { kind: "configuration", field: "realm" },
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: ({ instanceUrl, realm }) =>
          instanceUrl
            ? `${instanceUrl.replace(/\/+$/, "")}/realms/${encodeURIComponent(realm)}/protocol/openid-connect/token`
            : "",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => [],
      },
      defaultUrlPattern: ({ instanceUrl }) =>
        instanceUrl
          ? exactUrlToRegex(`${instanceUrl.replace(/\/+$/, "")}/admin`)
          : "",
      connectionTest: {
        protocol: "http",
        request: ({ instanceUrl }) =>
          `${instanceUrl.replace(/\/+$/, "")}/admin/realms`,
      },
    }),
  ],
});
